/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useRef, useEffect } from 'react'
import { FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { TwoFactorType } from 'models/authentication'
import { useAddTwoFactorMutation, useCreateTwoFactorMutation } from 'api/account'

import CreateTwoFactorVerificationCodeForm from 'containers/account/form/createTwoFactorVerificationCodeForm'
import AddTwoFactorCodeForm from 'containers/account/form/addTwoFactorCodeForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ConfigureEmailAuthLightbox({ status, close }: Props) {
    const createFormRef = useRef<FormikProps<{ identifier: string }>>(null)
    const addFormRef = useRef<FormikProps<{ code: string }>>(null)
    const [, { isLoading: isCreatingTwoFactor, isSuccess, reset }] = useCreateTwoFactorMutation({ fixedCacheKey: 'create-two-factor-email' })
    const [, { isLoading: isAddingTwoFactor, isSuccess: isAddingTwoFactorSuccess }] = useAddTwoFactorMutation({ fixedCacheKey: 'add-two-factor-email' })

    /**********************************************************************************************************
     *   COMPONENTS
     **********************************************************************************************************/
    function Content() {
        const twoFactor = {
            type: 'email' as TwoFactorType,
            validationSchema: Yup.object({
                identifier: Yup.string()
                    .required('Required')
                    .email('Invalid email address')
            }),
            field: {
                label: 'Email Address',
                type: 'email',
                autoComplete: 'email'
            }
        }

        if (isSuccess) {
            return <AddTwoFactorCodeForm form={addFormRef} twoFactorType={'email'} />
        }

        return <CreateTwoFactorVerificationCodeForm form={createFormRef} twoFactor={twoFactor} />
    }

    useEffect(() => {
        if (isAddingTwoFactorSuccess) {
            close()
        }
    }, [isAddingTwoFactorSuccess])

    return (
        <Lightbox
            title={'Email Verification'}
            description={'Your two-factor code will be sent to the email address you enter below.'}
            content={<Content />}
            conditions={{
                state: status,
                action: () => {
                    close()
                    reset()
                }
            }}
            actions={[
                {
                    label: 'Back',
                    color: 'secondary',
                    type: 'submit',
                    disabled: isCreatingTwoFactor || isAddingTwoFactor,
                    func: () => {
                        close()
                        reset()
                    }
                },
                {
                    label: isSuccess ? 'Add' : 'Continue',
                    color: 'primary',
                    type: 'submit',
                    form: isSuccess ? 'addTwoFactorCodeForm' : 'createTwoFactorVerificationCodeForm',
                    loading: isCreatingTwoFactor || isAddingTwoFactor
                }
            ]}
        />
    )
}
